import React, { useCallback, useEffect, useState } from 'react';
import {
  createSalePriceType,
  deleteSalePriceType,
  getSalePriceTypes,
  updateSalePriceType,
} from '../../services/salesPriceTypeService';
import ConfirmModal from '../Common/ConfirmModal';
import MutateGate from '../Common/MutateGate';

const CheckIcon = () => (
  <svg className="h-3.5 w-3.5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const FormCheckboxOption = ({ checked, onChange, disabled, title, description }) => (
  <label
    className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
      disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
    } ${
      checked
        ? 'border-teal-300 bg-teal-50/80 shadow-sm'
        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
    }`}
  >
    <input
      type="checkbox"
      className="sr-only"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <span
      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-all ${
        checked ? 'border-teal-600 bg-teal-600 shadow-sm' : 'border-gray-300 bg-white'
      }`}
      aria-hidden="true"
    >
      {checked ? <CheckIcon /> : null}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-medium text-gray-800">{title}</span>
      {description ? (
        <span className="block text-xs text-gray-500 mt-0.5 leading-relaxed">{description}</span>
      ) : null}
    </span>
  </label>
);

const emptyForm = { name: '', is_default: false, is_active: true };

const PriceListSaleTypesSection = ({ activePosId, onToast, onTypesChanged }) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadTypes = useCallback(async () => {
    if (!activePosId) {
      setTypes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await getSalePriceTypes(Number(activePosId));
      if (res.success) {
        setTypes(res.data || []);
      }
    } catch (error) {
      onToast(error.response?.data?.message || 'Error al cargar tipos de venta', 'error');
    } finally {
      setLoading(false);
    }
  }, [activePosId, onToast]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, is_default: types.length === 0 });
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name,
      is_default: row.is_default === true,
      is_active: row.is_active !== false,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activePosId) return;
    const name = form.name.trim();
    if (!name) {
      onToast('Indique el nombre del tipo de venta', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        point_of_sale_id: Number(activePosId),
        name,
        is_active: form.is_active,
        is_default: form.is_default,
      };
      const res = editing
        ? await updateSalePriceType(editing.id, payload)
        : await createSalePriceType(payload);
      if (res.success) {
        onToast(res.message, 'success');
        setShowForm(false);
        setEditing(null);
        await loadTypes();
        onTypesChanged?.();
      }
    } catch (error) {
      onToast(error.response?.data?.message || 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      const res = await deleteSalePriceType(deleteTarget.id);
      if (res.success) {
        onToast(res.message, 'success');
        setDeleteTarget(null);
        await loadTypes();
        onTypesChanged?.();
      }
    } catch (error) {
      onToast(error.response?.data?.message || 'Error al eliminar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (row) => {
    if (row.is_default) return;
    setSaving(true);
    try {
      const res = await updateSalePriceType(row.id, { is_default: true });
      if (res.success) {
        onToast('Tipo de venta predeterminado actualizado', 'success');
        await loadTypes();
        onTypesChanged?.();
      }
    } catch (error) {
      onToast(error.response?.data?.message || 'Error al actualizar', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!activePosId) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">
        Elija un punto de venta para configurar tipos de venta.
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">Cargando…</div>;
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          Defina los tipos de precio de este punto de venta (ej. Minorista, Mayorista). Luego asigne un precio por
          tipo en la pestaña <strong>Precios</strong>.
        </p>
        <MutateGate>
          <button
            type="button"
            onClick={openCreate}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700"
          >
            Nuevo tipo de venta
          </button>
        </MutateGate>
      </div>

      {types.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">
          No hay tipos de venta configurados. Cree al menos uno (por ejemplo, Minorista).
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Predeterminado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {types.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{row.name}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    {row.is_default ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                        Predeterminado
                      </span>
                    ) : (
                      <MutateGate>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => handleSetDefault(row)}
                          className="text-sm text-teal-600 hover:text-teal-800 disabled:opacity-50"
                        >
                          Marcar predeterminado
                        </button>
                      </MutateGate>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    {row.is_active ? 'Activo' : 'Inactivo'}
                  </td>
                  <td className="px-4 py-4 text-right text-sm space-x-3">
                    <MutateGate>
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="text-teal-600 hover:text-teal-800"
                      >
                        Editar
                      </button>
                      {!row.is_default && (
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(row)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Eliminar
                        </button>
                      )}
                    </MutateGate>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {editing ? 'Editar tipo de venta' : 'Nuevo tipo de venta'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="sale-type-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre
                </label>
                <input
                  id="sale-type-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Ej. Minorista, Mayorista"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                  maxLength={120}
                  required
                />
              </div>
              <div className="space-y-3">
                <FormCheckboxOption
                  checked={form.is_active}
                  disabled={editing?.is_default}
                  title="Activo"
                  description="Si está activo, aparecerá como columna en la pestaña Precios."
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                />
                {!editing?.is_default && (
                  <FormCheckboxOption
                    checked={form.is_default}
                    title="Usar como predeterminado"
                    description="Se usa para ofertas, rango mínimo y como precio base del producto."
                    onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}
                  />
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditing(null);
                  }}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Eliminar tipo de venta"
        message={`¿Eliminar "${deleteTarget?.name}"? Se borrarán los precios asociados a este tipo.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        confirmText="Eliminar"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </>
  );
};

export default PriceListSaleTypesSection;
